import React, { useEffect, useState } from "react";

import _ from "lodash";
import { connect } from "react-redux";
import styled from "styled-components";

import { usePrevious } from "app/components/hooks/usePrevious";
import { i18nFilter } from "app/filters/i18nFilter";
import { StyledSwitcherGranularityContainer } from "app/pages/segments/analysis/StyledComponents";
import { benchmarkVerticals } from "pro-features/pages/segments/components/benchmarkOvertime/benchmarkVerticals";
import { checkDurationIsValid } from "app/pages/segments/config/segmentsConfigHelpers";
import { SegmentsGroupAnalysisOverview } from "pro-features/pages/segments/SegmentsGroupAnalysisOverview";
import { NoDataSegments } from "app/pages/website-analysis/website-content/leading-folders/components/NoDataSegments";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";
import { AssetsService } from "app/services/AssetsService";
import DurationService from "app/services/DurationService";
import type {
    ICustomSegment,
    ICustomSegmentGroupWebsite,
    ICustomSegmentsGroup,
    ICustomSegmentsGroupAnalysisParams,
} from "app/services/segments/segmentsApiService";
import SegmentsApiService, { SEGMENT_TYPES } from "app/services/segments/segmentsApiService";
import type { ICustomSegmentMember } from "app/services/segments/SegmentsUtils";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { granularities } from "pro-features/utils";

import SegmentsGroupAnalysisTableContainer from "./segmentsGroupsAnalysisTableContainer/SegmentsGroupsAnalysisTableContainer";

const EmptyStateContainer = styled.div`
    padding: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background-color: #ffffff;
    box-shadow: 0 3px 6px 0 rgba(14, 30, 62, 0.08);
    width: 85%;
    max-width: 1368px;
    height: 386px;
    margin: 40px auto;
`;

interface ISegmentsGroupAnalysisOverviewContainerProps {
    customSegmentsData?: ICustomSegment[];
    selectedRows?: any;
    groups?: ICustomSegmentsGroup[];
}

const manipulateDataSettings = {
    // non-default settings for manipulating data
    defaults: {
        valueProp: "Average",
        winnerLeaderMax: true,
        winnerCompareFn: (a, b) => (a > b ? 1 : a < b ? -1 : 0),
    },
    columns: {
        Visits: {
            valueProp: "Total",
        },
        PagesViews: {
            valueProp: "Total",
        },
        BounceRate: {
            winnerLeaderMax: false,
        },
    },
};

const makeTableSegmentData = (segmentObj: ICustomSegmentMember, segmentType: SEGMENT_TYPES) => {
    let baseSegmentData: any = {
        SegmentType: segmentType,
        SegmentId: segmentObj.id,
    };
    switch (segmentType) {
        case SEGMENT_TYPES.SEGMENT:
            const actualSegmentObj = segmentObj as ICustomSegment;
            baseSegmentData = {
                ...baseSegmentData,
                SegmentName: actualSegmentObj.segmentName,
                SegmentDomain: actualSegmentObj.domain,
                SegmentLastUpdated: actualSegmentObj.lastUpdated,
                SegmentFavicon: actualSegmentObj.favicon,
            };
            break;
        case SEGMENT_TYPES.WEBSITE:
            const actualWebsiteObj = segmentObj as ICustomSegmentGroupWebsite;
            baseSegmentData = {
                ...baseSegmentData,
                SegmentDomain: actualWebsiteObj.domain,
                SegmentFavicon: actualWebsiteObj.favicon,
            };
    }
    return baseSegmentData;
};

const aggregateWinnerValue = (period, key, val, acc) => {
    const curMaxValue = _.get(acc.maxValues, [period, key]);
    const winnerCompareFn =
        manipulateDataSettings.columns[key]?.winnerCompareFn ?? manipulateDataSettings.defaults.winnerCompareFn;
    const winnerLeaderMax =
        manipulateDataSettings.columns[key]?.winnerLeaderMax ?? manipulateDataSettings.defaults.winnerLeaderMax;
    if (curMaxValue === undefined || winnerCompareFn(val, curMaxValue) === (winnerLeaderMax ? 1 : -1)) {
        _.set(acc.maxValues, [period, key], val);
    }
};

const aggregateSumTotal = (period, key, val, acc) => {
    _.set(acc.sumValues, [period, key], _.get(acc.sumValues, [period, key], 0) + val);
};

const manipulateData = (response, { customSegmentsData, availableSegmentWebsites = [] }) => {
    const aggregatedData = Object.keys(response).reduce(
        (acc, segId) => {
            const segData = response[segId];

            if (!segData) {
                return acc;
            }

            Object.keys(segData).forEach((tabId) => {
                const segDataTab = segData?.[tabId] || {};

                Object.keys(segDataTab).forEach((periodId) => {
                    const valueProp =
                        manipulateDataSettings.columns[tabId]?.valueProp ?? manipulateDataSettings.defaults.valueProp;
                    const { [valueProp]: sectValue } = segDataTab[periodId];

                    if (acc && !acc.segmentsAnalyzed.has(segId)) {
                        // aggregate data for segment meta
                        acc.segmentsAnalyzed.add(segId);
                        aggregateWinnerValue(periodId, "TrafficShare", segData?.Meta?.TrafficShare, acc);
                    }

                    if (sectValue > 0) {
                        // aggregate data for all non empty values
                        aggregateWinnerValue(periodId, tabId, sectValue, acc);
                        if (["Visits"].includes(tabId)) {
                            aggregateSumTotal(periodId, tabId, sectValue, acc);
                        }
                    }
                });
            });

            return acc;
        },
        { maxValues: {}, sumValues: {}, segmentsAnalyzed: new Set() },
    );

    const manipulatedDataRes = Object.keys(response).reduce(
        (acc, segId) => {
            const segData = response[segId];
            const [segmentObj, segmentType] = SegmentsUtils.getSegmentObjectByKey(segId, {
                segments: customSegmentsData,
                websites: availableSegmentWebsites,
            });
            if (!segmentObj) {
                acc.missingData.push(segId);
                return acc;
            }
            const addWinner = (period, key, val) => {
                const maxValue = _.get(aggregatedData?.maxValues, [period, key]);
                if (maxValue !== undefined && val === maxValue) {
                    _.get(acc.table, [period, segId, "winnersList"], []).push(key);
                }
            };
            Object.keys(segData).forEach((tabId) => {
                const segDataTab = segData[tabId];
                Object.keys(segDataTab).forEach((periodId) => {
                    if (acc && tabId !== "Meta" && !_.has(acc.table, [periodId, segId])) {
                        // add initial data for segment
                        const segTrafficShare = segData?.Meta?.TrafficShare;
                        const segmentData: any = {
                            ...makeTableSegmentData(segmentObj, segmentType),
                            TrafficShare: segTrafficShare,
                            winnersList: [],
                        };
                        _.set(acc.table, [periodId, segId], segmentData);
                        addWinner(periodId, "TrafficShare", segTrafficShare);
                    }

                    const valueProp =
                        manipulateDataSettings.columns[tabId]?.valueProp ?? manipulateDataSettings.defaults.valueProp;
                    const {
                        Graph: graphSect,
                        [valueProp]: sectValue,
                        Confidence: avgConfidence,
                    } = segDataTab[periodId];
                    if (graphSect) {
                        _.set(acc.graph, [periodId, tabId, segId], graphSect);
                    }
                    if (sectValue > 0) {
                        // only consider non empty values
                        _.set(acc.table, [periodId, segId, tabId], sectValue);
                        _.set(acc.table, [periodId, segId, "Confidence"], avgConfidence);

                        // check if leader
                        addWinner(periodId, tabId, sectValue);

                        // set relative group share
                        if (tabId === "Visits") {
                            const totalVisits = _.get(aggregatedData?.sumValues, [periodId, tabId], 0);
                            _.set(acc.table, [periodId, segId, "GroupVisitsShare"], sectValue / totalVisits);
                        }
                    }
                });
            });
            return acc;
        },
        { graph: {}, table: {}, missingData: [] },
    );

    return manipulatedDataRes;
};

const SegmentsGroupAnalysisOverviewContainer: React.FunctionComponent<ISegmentsGroupAnalysisOverviewContainerProps> = (
    props,
) => {
    const [selectedTab, setSelectedTab] = React.useState(benchmarkVerticals.Visits.dataKey);

    const pageFilters = proNavigator.getApiParams();
    const { duration, webSource = devicesTypes.DESKTOP } = proNavigator.getParams();
    const segmentsApiService = React.useMemo(() => new SegmentsApiService(), []);
    const [isLoading, setIsLoading] = React.useState(false);
    const durationRaw = DurationService.getDurationData(duration).raw;
    const monthDiff = durationRaw.to.diff(durationRaw.from, "month");
    const [graphGranularity, setGraphGranularity] = useState(
        (pageFilters.isWindow || monthDiff < 1) && webSource !== devicesTypes.MOBILE
            ? granularities[1]
            : granularities[2],
    );
    const [graphAvailableGran, setGraphAvailableGran] = useState([
        { title: "D", disabled: false },
        { title: "W", disabled: false },
        { title: "M", disabled: pageFilters.isWindow || monthDiff < 1 },
    ]);
    const [data, setData] = React.useState<any>(null);
    const [response, setResponse] = React.useState<any>(null);

    const { id, country, from, to, isWindow } = pageFilters;
    const group = props.groups?.find((grp) => grp.id === id);

    const previous: any = usePrevious({ pageFilters, graphGranularity });
    useEffect(() => {
        const durationRaw = DurationService.getDurationData(duration).raw;
        const monthDiff = durationRaw.to.diff(durationRaw.from, "month");
        if (response && graphGranularity !== previous.graphGranularity) {
            const manipulatedData = manipulateData(response, {
                customSegmentsData: props.customSegmentsData,
                availableSegmentWebsites: group?.websites,
            });
            setData({
                graphData: { Data: manipulatedData.graph[graphGranularity] },
                tableData: { Data: manipulatedData.table[granularities[2]] },
                missingData: manipulatedData.missingData,
            });
        }
        if (selectedTab === benchmarkVerticals.UniqueUsers.dataKey) {
            setGraphGranularity(granularities[2]);
            setGraphAvailableGran([
                { title: "D", disabled: true },
                { title: "W", disabled: true },
                { title: "M", disabled: false },
            ]);
        } else if (monthDiff < 1 && webSource !== devicesTypes.MOBILE && graphGranularity !== granularities[1]) {
            if (graphGranularity === granularities[2]) {
                setGraphGranularity(granularities[1]);
            }
            setGraphAvailableGran([
                { title: "D", disabled: webSource === devicesTypes.MOBILE },
                { title: "W", disabled: webSource === devicesTypes.MOBILE },
                { title: "M", disabled: true },
            ]);
        } else {
            setGraphAvailableGran([
                {
                    title: "D",
                    disabled: webSource === devicesTypes.MOBILE,
                },
                {
                    title: "W",
                    disabled: webSource === devicesTypes.MOBILE,
                },
                {
                    title: "M",
                    disabled:
                        (pageFilters.isWindow || monthDiff < 1) && webSource !== devicesTypes.MOBILE ? true : false,
                },
            ]);
        }
    }, [duration, graphGranularity, group?.websites, selectedTab]);
    React.useEffect(() => {
        (async () => {
            // get api params with defaults:
            const apiParams = {
                webSource: devicesTypes.DESKTOP,
                isWindow,
                timeGranularity: isWindow ? granularities[0] : granularities[2],
                ...pageFilters,
            };
            setIsLoading(true);
            try {
                const response =
                    duration === "28d" || webSource !== devicesTypes.DESKTOP
                        ? await Promise.all([
                              segmentsApiService.getCustomSegmentsGroupAnalysis({
                                  segmentGroupId: apiParams.id,
                                  country: apiParams.country,
                                  from: apiParams.from,
                                  to: apiParams.to,
                                  webSource: apiParams.webSource,
                                  isWindow: apiParams.isWindow,
                                  includeSubDomains: true,
                                  timeGranularity: apiParams.timeGranularity,
                                  lastUpdated: group.lastUpdatedTime,
                              } as ICustomSegmentsGroupAnalysisParams),
                          ])
                        : await Promise.all([
                              segmentsApiService.getCustomSegmentsGroupAnalysis({
                                  segmentGroupId: apiParams.id,
                                  country: apiParams.country,
                                  from: apiParams.from,
                                  to: apiParams.to,
                                  webSource: apiParams.webSource,
                                  isWindow: apiParams.isWindow,
                                  includeSubDomains: true,
                                  timeGranularity: apiParams.timeGranularity,
                                  lastUpdated: group.lastUpdatedTime,
                              } as ICustomSegmentsGroupAnalysisParams),
                              segmentsApiService.getUniqueUsersData({
                                  country: apiParams.country,
                                  from: apiParams.from,
                                  to: apiParams.to,
                                  compareFrom: undefined,
                                  compareTo: undefined,
                                  includeSubDomains: true,
                                  isWindow: apiParams.isWindow,
                                  keys: null,
                                  segmentGroupId: apiParams.id,
                                  timeGranularity: apiParams.timeGranularity,
                                  webSource: devicesTypes.DESKTOP,
                                  lastUpdated: group.lastUpdatedTime,
                              }),
                          ]);

                let responseJoinedUniqueUsers;
                if (duration !== "28d" && webSource === devicesTypes.DESKTOP) {
                    responseJoinedUniqueUsers = Object.keys(response[0]).reduce((acc: object, segId: string, idx) => {
                        const newResponse = response[0];
                        const uniqueUsersGraph = response[1][segId]?.UniqueUsers?.Monthly?.Graph;
                        const visitsGraph = response[0][segId]?.Visits?.Monthly?.Graph;

                        uniqueUsersGraph?.forEach((val, index) => {
                            val.Value = {
                                Value: val?.Value,
                                Confidence: visitsGraph[index]?.Value?.Confidence,
                            };
                        });

                        // adding unique users into the segmentData result
                        Object.assign(newResponse[segId], response[1][segId]);

                        // adding confidence into unique users:
                        const Confidence = newResponse[segId]?.Visits?.Monthly?.Confidence;
                        Confidence &&
                            Object.assign(newResponse[segId]?.UniqueUsers?.Monthly, {
                                Confidence: Confidence,
                            });

                        // adding the currect graph stracture for unique users:
                        uniqueUsersGraph &&
                            Object.assign(newResponse[segId].UniqueUsers?.Monthly.Graph, uniqueUsersGraph);

                        acc[segId] = newResponse[segId];
                        return acc;
                    }, {});
                }

                const manipulatedData = manipulateData(
                    duration === "28d" || webSource !== devicesTypes.DESKTOP ? response[0] : responseJoinedUniqueUsers,
                    {
                        customSegmentsData: props.customSegmentsData,
                        availableSegmentWebsites: group?.websites,
                    },
                );

                setData({
                    graphData: { Data: manipulatedData.graph[graphGranularity] },
                    tableData: { Data: manipulatedData.table[granularities[2]] },
                    missingData: manipulatedData.missingData,
                });
                setResponse(duration !== "28d" && responseJoinedUniqueUsers ? responseJoinedUniqueUsers : response[0]);
            } catch {
                setData({
                    graphData: { Data: {} },
                    tableData: { Data: {} },
                    missingData: [],
                });
            } finally {
                setIsLoading(false);
            }
        })();
    }, []); // initialize effect, since page is recreated on route params change

    // get which advanced segments are omitted from response
    const omittedAdvancedSegments = React.useMemo(() => {
        if (!response) {
            return [];
        }
        const responseSegmentsIds = Object.keys(response);
        const omittedSegmentsKeys = group.members.filter((member) => !responseSegmentsIds.includes(member));
        const omittedSegmentsObjs = SegmentsUtils.getSegmentObjectsByKeys(omittedSegmentsKeys, {
            segments: props.customSegmentsData,
            websites: group?.websites,
        });
        return omittedSegmentsObjs
            .filter(([segObj, segType]) => segType === SEGMENT_TYPES.SEGMENT)
            .map(([segObj, segType]) => makeTableSegmentData(segObj, segType));
    }, [response]);

    const GraphSwitcherComponent = () => {
        return (
            <StyledSwitcherGranularityContainer
                itemList={graphAvailableGran}
                selectedIndex={granularities.indexOf(graphGranularity)}
                onItemClick={onTimeGranularityClick}
                className={"gran-switch"}
            />
        );
    };

    const renderEmpty = React.useMemo(() => {
        let hasDataSegment = false;
        if (data?.tableData) {
            hasDataSegment = Object.values(data.tableData.Data).some(
                (seg) => seg["TrafficShare"] > 0 || (seg["SegmentType"] === "WEBSITE" && seg["Visits"] > 0),
            );
        }
        return !hasDataSegment;
    }, [data]);

    const pageProps = {
        loading: isLoading,
        components: { SegmentsGroupAnalysisTableContainer, GraphSwitcherComponent },
        responseMissingData: data?.missingData,
        omittedAdvancedSegments,
        pageFilters,
        timeGranularity: graphGranularity,
        setSelectedTab,
        availableMembers: {
            segments: props.customSegmentsData,
            websites: group?.websites,
        },
        selectedRows: props.selectedRows,
        graphData: data?.graphData,
        onGraphDDClick: (obj) => console.log("downloading graph " + obj.id),
        tableExcelLink: segmentsApiService.getCategoryConversionExcel({
            segmentGroupId: id,
            country,
            from,
            to,
            webSource,
            timeGranularity: graphGranularity,
            isWindow,
            includeSubDomains: true,
            fileName: `SegmentsComparison-(${group?.name})-(${country})-(${from})-(${to})`,
            lastUpdated: group?.lastUpdatedTime,
        }),
        tableData: data?.tableData,
        getAssetsUrl: AssetsService.assetUrl.bind(AssetsService),
        tableDataOmitted: omittedAdvancedSegments,
    };

    const onTimeGranularityClick = (index) => {
        const timeGranularity = granularities[index];
        if (graphGranularity !== timeGranularity) {
            setGraphGranularity(timeGranularity);
        }
    };

    if (!checkDurationIsValid()) {
        return null;
    }

    return renderEmpty && !isLoading ? (
        <EmptyStateContainer>
            <NoDataSegments
                messageBottomHeader={i18nFilter()("segments.nodata.notavilable")}
                messageBottomText={i18nFilter()("segments.nodata.notavilable.subtitle")}
            />
        </EmptyStateContainer>
    ) : (
        <SegmentsGroupAnalysisOverview {...pageProps} />
    );
};

function mapStateToProps(state) {
    const {
        segmentsModule: { customSegmentsMeta },
    } = state;
    return {
        selectedRows: getTableSelectedItems("SegmentsGroupAnalysisTable")(state),
        customSegmentsData: customSegmentsMeta?.AccountSegments,
        groups: customSegmentsMeta?.SegmentGroups,
    };
}

export default connect(mapStateToProps, null)(SegmentsGroupAnalysisOverviewContainer);
